/**
 * Money helpers.
 *
 * The shared `pmc_pt_calc` database stores every amount as DECIMAL(18,2), and
 * the legacy PHP app computed on `float` with `round()`. To reproduce its
 * figures exactly we must match two PHP behaviours that JavaScript does not
 * share out of the box:
 *
 *   1. PHP `round()` rounds half AWAY FROM ZERO (round(-0.5) === -1.0),
 *      whereas `Math.round()` rounds half towards +Infinity (Math.round(-0.5) === -0).
 *   2. PHP's rounding is decimal-aware and immune to the binary artefacts that
 *      make `Math.round(1.005 * 100) / 100` return 1 instead of 1.01.
 *
 * `round2` below handles both. Every monetary value crossing the API boundary
 * is serialised as a JS `number` already rounded to 2 dp.
 */

/**
 * Multiply by 10^exp using the decimal exponent in the number's own string
 * form, so 1.005 becomes exactly 100.5 rather than 100.49999999999999.
 * Handles values already in exponential notation (1e-7) correctly.
 */
function shiftDecimal(value: number, exp: number): number {
  if (value === 0 || !Number.isFinite(value)) return value;
  const [mantissa = '0', e] = String(value).split('e');
  return Number(`${mantissa}e${(e ? Number(e) : 0) + exp}`);
}

/**
 * PHP-compatible rounding to `precision` decimal places.
 *
 * PHP rounds half AWAY FROM ZERO, so round(-1.005, 2) === -1.01 and
 * round(-2.5) === -3. `Math.round` rounds half towards +Infinity and would
 * give -1.00 and -2. We therefore round the magnitude and reapply the sign.
 */
export function roundTo(value: number, precision = 2): number {
  if (!Number.isFinite(value)) return 0;
  const sign = value < 0 ? -1 : 1;
  const scaled = shiftDecimal(Math.abs(value), precision);
  const rounded = shiftDecimal(Math.round(scaled), -precision);
  if (!Number.isFinite(rounded)) {
    const factor = 10 ** precision;
    return (sign * Math.round(Math.abs(value) * factor)) / factor;
  }
  return sign * rounded;
}

/** Round to 2 decimal places — the precision of every DECIMAL(18,2) column. */
export function round2(value: number): number {
  return roundTo(value, 2);
}

/**
 * mysql2 returns DECIMAL columns as strings to avoid precision loss. Use this
 * at every repository boundary rather than sprinkling `Number(...)` around.
 */
export function toAmount(value: unknown): number {
  if (value === null || value === undefined || value === '') return 0;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Indian digit grouping — 81,94,657.86 (lakh/crore), not 8,194,657.86.
 * Ported from the PHP `inr()` helper.
 */
export function formatAmount(amount: number, decimals = 2): string {
  const value = roundTo(amount, decimals);
  const sign = value < 0 ? '-' : '';
  const fixed = Math.abs(value).toFixed(decimals);
  const [intPart = '0', decPart] = fixed.split('.');

  let grouped = intPart;
  if (intPart.length > 3) {
    const last3 = intPart.slice(-3);
    const rest = intPart.slice(0, -3);
    grouped = `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}`;
  }
  return decPart ? `${sign}${grouped}.${decPart}` : `${sign}${grouped}`;
}

/** Same as `formatAmount` but prefixed with the rupee sign — the PHP `inr()`. */
export function formatINR(amount: number, decimals = 2): string {
  return `₹${formatAmount(amount, decimals)}`;
}

// ─── Amount in words (Indian system) ─────────────────────────────────────────
// Exact port of `_tw()` / `amountToWordsBill()` from includes/bill_engine.php.
// This string is printed on the official bill document, so it must match the
// PHP output character for character. Note the legacy behaviours preserved here:
//   - the amount is rounded to WHOLE RUPEES; paise are never spoken
//   - zero renders as "Zero Rupees", not "Zero Rupees Only"
//   - there is no "and" between groups
//   - the suffix is " Rupees Only", not a "Rupees ..." prefix

const ONES = [
  '', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
  'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
  'seventeen', 'eighteen', 'nineteen',
] as const;

const TENS = [
  '', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety',
] as const;

/** PHP `_tw()` — words for an integer below 100, lowercase. */
function twoDigitWords(n: number): string {
  if (n < 20) return ONES[n] ?? '';
  return `${TENS[Math.floor(n / 10)] ?? ''} ${ONES[n % 10] ?? ''}`.trim();
}

/** PHP `ucwords()` — capitalise the first letter of every space-separated word. */
function ucwords(s: string): string {
  return s.replace(/(^|\s)(\S)/g, (_m, lead: string, ch: string) => lead + ch.toUpperCase());
}

/**
 * "In Words" line for the bill document, e.g.
 * `Eighty One Lakh Ninety Four Thousand Six Hundred Fifty Seven Rupees Only`.
 */
export function amountToWordsBill(amount: number): string {
  let n = Math.trunc(roundTo(amount, 0));
  if (n === 0) return 'Zero Rupees';

  const crore = Math.trunc(n / 10000000); n %= 10000000;
  const lakh = Math.trunc(n / 100000); n %= 100000;
  const thousand = Math.trunc(n / 1000); n %= 1000;
  const hundred = Math.trunc(n / 100); n %= 100;

  let words = '';
  if (crore) words += `${twoDigitWords(crore)} Crore `;
  if (lakh) words += `${twoDigitWords(lakh)} Lakh `;
  if (thousand) words += `${twoDigitWords(thousand)} Thousand `;
  if (hundred) words += `${twoDigitWords(hundred)} Hundred `;
  if (n) words += `${twoDigitWords(n)} `;

  return `${ucwords(words.trim())} Rupees Only`;
}
